import {
  FormEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Form } from "react-bootstrap";
import axiosInstance, { getAPIURL } from "../../../api/axiosInstance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faApple } from "@fortawesome/free-brands-svg-icons";
import { FcGoogle } from "react-icons/fc";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  useMakeSubscriptionPayment,
  type CreatePaymentIntentResponse,
} from "../../../api/mutations/useMakeSubscriptionPayment";
import { useMakePayment } from "../../../api/mutations/useMakePayment";
import {
  usePreviewEventStripeDiscount,
  type PreviewEventStripeDiscountResponse,
} from "../../../api/mutations/usePreviewEventStripeDiscount";
import { useSavePayment } from "../../../api/mutations/useSavePayment";
import { useAppContext } from "../../../Context/AppContext";
import { Memberships } from "../../../types/Types";
import {
  logPaymentError,
  getPaymentErrorSource,
  getPaymentErrorMessage,
} from "../../../utils/paymentErrorLogger";
import {
  getUnsupportedWalletMessage,
  getWalletButtonVisibility,
} from "../../../utils/walletSupport";
import {
  buildRegistrationFormDataAfterPayment,
  persistRegistrationPaidMembershipPrice,
} from "../registrationCheckoutShared";
import {
  findMembershipForCheckout,
  resolveMembershipIdForName,
  syncRegistrationMembership,
} from "../registrationMembershipSync";
import { formatCurrencyAmount } from "../../../utils/formatCurrency";
import {
  convertGbpAmountMajor,
  fetchGbpToCurrency,
} from "../../../utils/exchangeRates";
import { EXPLORER_EVENT_PAYMENT_DONE_KEY } from "../../../explorer/explorerSession";
import {
  previewWalletPricing,
  type PreviewWalletPricingResponse,
} from "../../../api/mutations/usePreviewWalletPricing";
import { runWalletPaymentRequest } from "../../../utils/stripeExpressWalletPay";
import type { PaymentMethod as StripePaymentMethod } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";
import { useTranslateDynamicFields } from "../../../api/queries/useTranslateDynamicFields";
import {
  getEarlyStageStatus,
  resolveEarlyStageProduct,
  type EarlyStageResolvedProduct,
  type EarlyStageVerificationStatus,
} from "../../../api/earlyStageMembershipApi";
import { isEarlyStageMembership } from "../../../utils/membershipTier";
import EarlyStagePriceChoice from "../EarlyStagePriceChoice";
import { needsEarlyStagePricePlanSelection } from "../registrationResume";

const CONTAINER_MIN_HEIGHT = 420;
const CONTAINER_STYLE = { minHeight: CONTAINER_MIN_HEIGHT };

const CARD_ELEMENT_STYLE = {
  base: {
    fontSize: "16px" as const,
    color: "#000",
    "::placeholder": { color: "#999" },
  },
  invalid: { color: "red" as const },
};

const CARD_NUMBER_OPTIONS = {
  style: {
    base: CARD_ELEMENT_STYLE.base,
    invalid: CARD_ELEMENT_STYLE.invalid,
  },
};

const CARD_EXPIRY_OPTIONS = { style: { base: CARD_ELEMENT_STYLE.base } };
const CARD_CVC_OPTIONS = { style: { base: CARD_ELEMENT_STYLE.base } };

function minorToMajor(amountMinor: number, currency: string): number {
  const c = currency.trim().toLowerCase();
  if (
    c === "jpy" ||
    c === "vnd" ||
    c === "xaf" ||
    c === "xpf" ||
    c === "clp" ||
    c === "ugx"
  ) {
    return amountMinor;
  }
  return amountMinor / 100;
}

interface CheckoutStepProps {
  selectedMembership: string;
  isAnnualMembership: "monthly" | "annual";
  onNext: () => void;
  onBack: () => void;
  memberships: Memberships[];
  membershipPrice?: number;
  discountCode?: string;
  /** Explorer flow: event to book after membership + combined Stripe charge. */
  explorerComboEventId?: string | null;
  comboEventName?: string;
  comboEventPrice?: number;
  comboEventEarlyBird?: boolean;
  comboEventCatalogPrice?: number;
  comboEventEarlyBirdDiscountAmount?: number;
  comboEventMemberDiscountLabel?: string | null;
  comboEventMemberDiscountAmount?: number;
  /** ISO 4217 (e.g. gbp); must match membership currency for Explorer combo checkout. */
  comboEventCurrency?: string | null;
  comboEventProductId?: string;
  /** Explorer wizard branch for Stripe metadata (event | mentor | …). */
  explorerComboFlow?: string;
  /** When true, Explorer combo checkout shows Stripe event promotion code (validated in Stripe). */
  showExplorerEventStripePromo?: boolean;
  comboEventStripeDiscountCode?: string;
  onComboEventStripeDiscountCodeChange?: (code: string) => void;
  saveUserMutate: (data: FormData) => void;
  isSaveUserPending: boolean;
  /** Registration: price tier is chosen on a separate step before checkout. */
  hideEarlyStagePriceChoice?: boolean;
  /** Early Stage £10/month path without under-23 age verification. */
  earlyStageDirectStandardPath?: boolean;
  /** Upgrade/renew: do not persist membership tier until Pay Now succeeds. */
  skipPrePaymentMembershipSync?: boolean;
}

function CheckoutStepInner({
  selectedMembership,
  isAnnualMembership,
  onNext,
  onBack,
  memberships,
  membershipPrice = 0,
  discountCode,
  explorerComboEventId = null,
  comboEventName = "",
  comboEventPrice = 0,
  comboEventEarlyBird = false,
  comboEventCatalogPrice = 0,
  comboEventEarlyBirdDiscountAmount = 0,
  comboEventMemberDiscountLabel = null,
  comboEventMemberDiscountAmount = 0,
  comboEventCurrency = "gbp",
  comboEventProductId = "",
  explorerComboFlow = "",
  showExplorerEventStripePromo = false,
  comboEventStripeDiscountCode = "",
  onComboEventStripeDiscountCodeChange,
  saveUserMutate,
  isSaveUserPending,
  hideEarlyStagePriceChoice = false,
  earlyStageDirectStandardPath = false,
  skipPrePaymentMembershipSync = false,
}: CheckoutStepProps) {
  const { t, i18n } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { objectId: contextObjectId } = useAppContext();
  const [billingAddress, setBillingAddress] = useState("");
  const [saveCard, setSaveCard] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState<Record<string, any>>({});
  const effectiveObjectId =
    (typeof userData.objectId === "string" && userData.objectId.trim()
      ? userData.objectId.trim()
      : contextObjectId?.trim()) || "";
  const { mutateAsync: createSubscriptionAsync } = useMakeSubscriptionPayment();
  const { mutateAsync: makeEventPayment } = useMakePayment();
  const { mutate: savePaymentMethod } = useSavePayment();
  const { mutateAsync: previewComboEventDiscount } =
    usePreviewEventStripeDiscount();

  const [showComboEventPromo, setShowComboEventPromo] = useState(false);
  const [comboEventDiscountPreview, setComboEventDiscountPreview] =
    useState<PreviewEventStripeDiscountResponse | null>(null);
  /** GBP→event currency rate for display only (Explorer sequential, mixed currency). */
  const [fxRate, setFxRate] = useState<number | null>(null);
  const [fxRateDate, setFxRateDate] = useState<string | null>(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState<string | null>(null);
  const [membershipPromoPreview, setMembershipPromoPreview] =
    useState<PreviewWalletPricingResponse | null>(null);
  const [membershipPromoPreviewError, setMembershipPromoPreviewError] = useState<
    string | null
  >(null);
  const [earlyStageStatus, setEarlyStageStatus] =
    useState<EarlyStageVerificationStatus | null>(null);
  const [earlyStageProduct, setEarlyStageProduct] =
    useState<EarlyStageResolvedProduct | null>(null);
  const [earlyStageGateMessage, setEarlyStageGateMessage] = useState("");
  const [earlyStageMembershipSynced, setEarlyStageMembershipSynced] =
    useState(false);
  /** Set immediately when user picks £2/£10 (before resolve-product returns). */
  const [earlyStageChosenFee, setEarlyStageChosenFee] = useState<number | null>(
    null,
  );
  const isEarlyStageCheckout = isEarlyStageMembership(selectedMembership);
  /** Portal membership subscription is billed in GBP (matches backend subscription prices). */
  const membershipBillingCurrency = "gbp";
  const { showApplePay, showGooglePay } = getWalletButtonVisibility();

  const membershipProductIdForCheckout = useMemo(() => {
    const sel = findMembershipForCheckout(memberships, selectedMembership, {
      earlyStageProductId: earlyStageProduct?.productId,
      earlyStageMonthlyFeeGbp: earlyStageProduct?.monthlyFeeGbp,
    });
    if (!sel) return "";
    if (isEarlyStageCheckout) {
      if (earlyStageDirectStandardPath) {
        return (sel?.productId ?? "").trim();
      }
      return earlyStageProduct?.productId?.trim() ?? "";
    }
    return (
      isAnnualMembership === "annual"
        ? sel.productIdAnnual
        : sel.productId
    )?.trim() ?? "";
  }, [
    memberships,
    selectedMembership,
    isEarlyStageCheckout,
    earlyStageDirectStandardPath,
    earlyStageProduct,
    isAnnualMembership,
  ]);

  const membershipCatalogPriceMajor = useMemo(() => {
    const sel = findMembershipForCheckout(memberships, selectedMembership, {
      earlyStageProductId: earlyStageProduct?.productId,
      earlyStageMonthlyFeeGbp: earlyStageProduct?.monthlyFeeGbp,
    });
    if (!sel) return 0;
    const raw =
      isAnnualMembership === "annual" ? sel.annualFee : sel.monthlyFee;
    return Number(raw) || 0;
  }, [
    memberships,
    selectedMembership,
    isAnnualMembership,
    earlyStageProduct,
  ]);

  const resolvedEarlyStagePriceTier = useMemo(() => {
    const fromStatus = earlyStageStatus?.priceTier?.trim();
    if (fromStatus) return fromStatus;
    const fromUser =
      typeof userData.earlyStagePriceTier === "string"
        ? userData.earlyStagePriceTier.trim()
        : "";
    return fromUser;
  }, [earlyStageStatus?.priceTier, userData.earlyStagePriceTier]);

  const effectiveMembershipPrice = useMemo(() => {
    if (!isEarlyStageCheckout) {
      if (
        membershipPromoPreview?.amountMinor != null &&
        membershipPromoPreview.amountMinor >= 0
      ) {
        return minorToMajor(
          membershipPromoPreview.amountMinor,
          membershipPromoPreview.currency || membershipBillingCurrency,
        );
      }
      if (membershipPrice > 0) return membershipPrice;
      return membershipCatalogPriceMajor;
    }
    if (earlyStageDirectStandardPath) {
      if (
        membershipPromoPreview?.amountMinor != null &&
        membershipPromoPreview.amountMinor >= 0
      ) {
        return minorToMajor(
          membershipPromoPreview.amountMinor,
          membershipPromoPreview.currency || membershipBillingCurrency,
        );
      }
      if (membershipPrice > 0) return membershipPrice;
      return membershipCatalogPriceMajor;
    }
    if (earlyStageProduct != null && earlyStageProduct.monthlyFeeGbp > 0) {
      return earlyStageProduct.monthlyFeeGbp;
    }
    if (earlyStageChosenFee != null && earlyStageChosenFee > 0) {
      return earlyStageChosenFee;
    }
    const tier = resolvedEarlyStagePriceTier;
    if (tier === "Standard2") return 2;
    if (tier === "Premium10") return 10;
    if (membershipPrice > 0) return membershipPrice;
    return membershipCatalogPriceMajor;
  }, [
    isEarlyStageCheckout,
    earlyStageDirectStandardPath,
    membershipPrice,
    membershipCatalogPriceMajor,
    membershipPromoPreview,
    membershipBillingCurrency,
    earlyStageProduct,
    earlyStageChosenFee,
    resolvedEarlyStagePriceTier,
  ]);

  const membershipIdForCheckout = useMemo(
    () => resolveMembershipIdForName(memberships, selectedMembership),
    [memberships, selectedMembership],
  );

  const buildPaidMembershipFormData = useCallback(
    () =>
      buildRegistrationFormDataAfterPayment({
        effectiveObjectId,
        userData,
        discountCode: discountCode || "",
        membershipName: selectedMembership,
        membershipId: membershipIdForCheckout,
        isAnnualMembership: isAnnualMembership === "annual" ? 1 : 0,
      }),
    [
      effectiveObjectId,
      userData,
      discountCode,
      selectedMembership,
      membershipIdForCheckout,
      isAnnualMembership,
    ],
  );

  useEffect(() => {
    if (!effectiveObjectId || !selectedMembership?.trim()) {
      setEarlyStageMembershipSynced(false);
      return;
    }

    if (skipPrePaymentMembershipSync) {
      setEarlyStageMembershipSynced(true);
      return;
    }

    let cancelled = false;
    setEarlyStageMembershipSynced(false);

    const membershipId = resolveMembershipIdForName(
      memberships,
      selectedMembership,
    );

    syncRegistrationMembership({
      objectId: effectiveObjectId,
      membershipName: selectedMembership,
      membershipId,
      isAnnualMembership: isAnnualMembership === "annual" ? 1 : 0,
    })
      .then(() => {
        if (!cancelled) setEarlyStageMembershipSynced(true);
      })
      .catch(() => {
        if (!cancelled) {
          if (isEarlyStageCheckout) {
            setEarlyStageGateMessage(
              "Could not save your membership selection. Please go back and try again.",
            );
          }
          setEarlyStageMembershipSynced(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    isEarlyStageCheckout,
    effectiveObjectId,
    selectedMembership,
    memberships,
    isAnnualMembership,
    skipPrePaymentMembershipSync,
  ]);

  const refreshEarlyStageCheckout = useCallback(async () => {
    if (!isEarlyStageCheckout || !effectiveObjectId) return;
    if (earlyStageDirectStandardPath) {
      setEarlyStageGateMessage("");
      setEarlyStageChosenFee(
        membershipPrice > 0 ? membershipPrice : membershipCatalogPriceMajor || 10,
      );
      return;
    }
    setEarlyStageGateMessage("");
    try {
      const status = await getEarlyStageStatus();
      setEarlyStageStatus(status);
      if (!status.canPay) {
        if (status.requiresAdminApproval && !status.canPay) {
          setEarlyStageGateMessage(
            "Your ID is awaiting admin approval. Continue with £10/month from the verification step, or wait until you are approved."
          );
        } else if (status.status === "Rejected") {
          setEarlyStageGateMessage(
            "Your Young Professional application was rejected. You can continue at £10/month. Questions: enquiries@smbf.global."
          );
        } else if (
          status.status === "Approved" &&
          status.ageTier === "TwentyThreePlus"
        ) {
          setEarlyStageGateMessage(
            "Please confirm your date of birth on the Age Verification step before paying."
          );
        } else {
          setEarlyStageGateMessage(
            "Please complete Early Stage membership verification before payment."
          );
        }
        setEarlyStageProduct(null);
        return;
      }
      if (needsEarlyStagePricePlanSelection(status)) {
        setEarlyStageProduct(null);
        setEarlyStageGateMessage(
          hideEarlyStagePriceChoice
            ? "Please choose £2/month or £10/month on the Payment Plan step before paying."
            : "",
        );
        return;
      }
      const resolved = await resolveEarlyStageProduct();
      setEarlyStageProduct(resolved);
      if (resolved.monthlyFeeGbp > 0) {
        setEarlyStageChosenFee(resolved.monthlyFeeGbp);
      }
    } catch (err: unknown) {
      const apiMessage = getPaymentErrorMessage(err);
      setEarlyStageGateMessage(
        apiMessage.trim()
          ? apiMessage
          : "Could not load Early Stage payment details.",
      );
      setEarlyStageProduct(null);
    }
  }, [
    hideEarlyStagePriceChoice,
    isEarlyStageCheckout,
    effectiveObjectId,
    earlyStageDirectStandardPath,
    membershipPrice,
    membershipCatalogPriceMajor,
  ]);

  useEffect(() => {
    if (!earlyStageMembershipSynced) return;
    void refreshEarlyStageCheckout();
  }, [earlyStageMembershipSynced, refreshEarlyStageCheckout]);
  const uiText = useMemo(
    () => ({
      checkout: "Checkout",
      annualMembership: "Annual Membership",
      monthlyMembership: "Monthly Membership",
      cardNumber: "Card Number",
      expiry: "Expiry",
      cvc: "CVC",
      billingAddressOptional: "Billing Address (optional)",
      billingAddress: "Billing Address",
      saveCardForFuture: "Save this card for future payments",
      payNow: "Pay Now",
      processing: "Processing...",
      selectedMembershipBusiness: "Business Membership",
      selectedMembershipExecutive: "Executive Membership",
      selectedMembershipYouth: "Youth Membership",
      discount24off: "24 month free",
      discount12off: "12 month free",
      discountWaive6: "6 month free",
      discountComp3: "3 month free",
      discount1comp: "1 month free",
      discount53off: "50% off for 3 months",
      discount56off: "50% off for 6 months",
      discount512off: "50% off for 12 months",
      promoVerifyFailed: "Could not verify promotion code.",
      chargedAs: "Charged as",
      onStripe: "on Stripe",
      pay: "Pay",
    }),
    []
  );
  const { data: translatedUiText } = useTranslateDynamicFields(
    uiText,
    i18n.resolvedLanguage || i18n.language
  );
  const tt = useCallback(
    (key: keyof typeof uiText) => translatedUiText?.[key] || uiText[key],
    [translatedUiText, uiText]
  );
  const selectedMembershipLabel = useMemo(() => {
    if (selectedMembership === "Business Membership") {
      return translatedUiText?.selectedMembershipBusiness || t("membership.businessTitle");
    }
    if (selectedMembership === "Executive Membership") {
      return translatedUiText?.selectedMembershipExecutive || t("membership.executiveTitle");
    }
    if (selectedMembership === "Youth Membership") {
      return translatedUiText?.selectedMembershipYouth || t("membership.youthTitle");
    }
    if (selectedMembership === "Early Stage Membership") {
      return t("membership.earlyStageTitle");
    }
    return selectedMembership;
  }, [selectedMembership, translatedUiText, t]);

  const showExplorerComboEventSection = Boolean(
    explorerComboEventId?.trim() &&
      (comboEventPrice > 0 || comboEventMemberDiscountAmount > 0),
  );
  const comboCurrency = (comboEventCurrency || "gbp").trim() || "gbp";
  /**
   * Keep a single Stripe subscription invoice for Explorer combo when currencies match
   * (GBP membership + GBP event). Fall back to sequential two-charge flow only when
   * mixed currencies make a single invoice impossible.
   */
  const shouldRunExplorerSequentialEventPayment = Boolean(
    showExplorerComboEventSection &&
      comboEventProductId.trim() &&
      membershipBillingCurrency.toLowerCase() !== comboCurrency.toLowerCase(),
  );

  const comboIdNum = explorerComboEventId?.trim()
    ? parseInt(explorerComboEventId.trim(), 10)
    : NaN;

  const membershipListPriceMajor = useMemo(() => {
    if (
      membershipPromoPreview?.listAmountMinor != null &&
      membershipPromoPreview.listAmountMinor > 0
    ) {
      return minorToMajor(
        membershipPromoPreview.listAmountMinor,
        membershipPromoPreview.currency || membershipBillingCurrency,
      );
    }
    if (isEarlyStageCheckout && effectiveMembershipPrice > 0) {
      return effectiveMembershipPrice;
    }
    return membershipCatalogPriceMajor;
  }, [
    membershipPromoPreview,
    membershipBillingCurrency,
    isEarlyStageCheckout,
    effectiveMembershipPrice,
    membershipCatalogPriceMajor,
  ]);

  const membershipPromoDiscountMajor = useMemo(() => {
    if (
      membershipPromoPreview?.discountAmountMinor != null &&
      membershipPromoPreview.discountAmountMinor > 0
    ) {
      return minorToMajor(
        membershipPromoPreview.discountAmountMinor,
        membershipPromoPreview.currency || membershipBillingCurrency,
      );
    }
    const code = (discountCode || "").trim();
    if (!code) return 0;
    return Math.max(0, membershipListPriceMajor - effectiveMembershipPrice);
  }, [
    membershipPromoPreview,
    discountCode,
    membershipListPriceMajor,
    effectiveMembershipPrice,
    membershipBillingCurrency,
  ]);

  const membershipPromoSummary = useMemo(
    () =>
      membershipPromoPreview?.promotionSummary?.trim() ||
      membershipPromoPreview?.label?.trim() ||
      null,
    [membershipPromoPreview],
  );

  const showMembershipPromoBreakdown = useMemo(
    () =>
      Boolean(
        (discountCode || "").trim() && membershipPromoDiscountMajor > 0.001,
      ),
    [discountCode, membershipPromoDiscountMajor],
  );

  const comboDisplayPrice = useMemo(() => {
    const trimmed = (comboEventStripeDiscountCode || "").trim();
    if (
      trimmed &&
      comboEventDiscountPreview?.valid &&
      comboEventDiscountPreview.finalTotal != null
    ) {
      return comboEventDiscountPreview.finalTotal;
    }
    return comboEventPrice;
  }, [
    comboEventStripeDiscountCode,
    comboEventDiscountPreview,
    comboEventPrice,
  ]);

  const checkoutTotalToday = useMemo(() => {
    if (showExplorerComboEventSection) {
      return effectiveMembershipPrice + comboDisplayPrice;
    }
    return effectiveMembershipPrice;
  }, [
    showExplorerComboEventSection,
    effectiveMembershipPrice,
    comboDisplayPrice,
  ]);

  /** Membership-only amount charged today (for confirmation page). */
  const confirmedMembershipPriceMajor = useMemo(() => {
    if (showMembershipPromoBreakdown) {
      return Math.max(0, membershipListPriceMajor - membershipPromoDiscountMajor);
    }
    return effectiveMembershipPrice;
  }, [
    showMembershipPromoBreakdown,
    membershipListPriceMajor,
    membershipPromoDiscountMajor,
    effectiveMembershipPrice,
  ]);

  const showCheckoutTotal = useMemo(
    () =>
      showExplorerComboEventSection ||
      shouldRunExplorerSequentialEventPayment ||
      showMembershipPromoBreakdown ||
      effectiveMembershipPrice > 0.001,
    [
      showExplorerComboEventSection,
      shouldRunExplorerSequentialEventPayment,
      showMembershipPromoBreakdown,
      effectiveMembershipPrice,
    ],
  );

  const membershipAndEventSameCurrency = useMemo(
    () =>
      membershipBillingCurrency.toLowerCase() === comboCurrency.toLowerCase(),
    [comboCurrency],
  );

  const membershipShownInEventCurrency = useMemo(() => {
    if (membershipAndEventSameCurrency) return effectiveMembershipPrice;
    if (fxRate == null || !Number.isFinite(fxRate)) return null;
    return convertGbpAmountMajor(effectiveMembershipPrice, fxRate);
  }, [effectiveMembershipPrice, fxRate, membershipAndEventSameCurrency]);

  const totalShownInEventCurrency = useMemo(() => {
    if (membershipAndEventSameCurrency) {
      return effectiveMembershipPrice + comboDisplayPrice;
    }
    if (membershipShownInEventCurrency == null) return null;
    return (
      Math.round((membershipShownInEventCurrency + comboDisplayPrice) * 100) /
      100
    );
  }, [
    membershipAndEventSameCurrency,
    membershipShownInEventCurrency,
    comboDisplayPrice,
    effectiveMembershipPrice,
  ]);

  useEffect(() => {
    if (
      !shouldRunExplorerSequentialEventPayment ||
      membershipAndEventSameCurrency
    ) {
      setFxRate(null);
      setFxRateDate(null);
      setFxLoading(false);
      setFxError(null);
      return;
    }

    let cancelled = false;
    setFxLoading(true);
    setFxError(null);

    void (async () => {
      try {
        const { rate, date } = await fetchGbpToCurrency(comboCurrency);
        if (!cancelled) {
          setFxRate(rate);
          setFxRateDate(date);
          setFxLoading(false);
        }
      } catch {
        if (!cancelled) {
          setFxRate(null);
          setFxRateDate(null);
          setFxError(
            t("checkout.couldNotLoadExchangeRate"),
          );
          setFxLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    shouldRunExplorerSequentialEventPayment,
    membershipAndEventSameCurrency,
    comboCurrency,
    t,
  ]);

  useEffect(() => {
    if (
      !showExplorerEventStripePromo ||
      !Number.isFinite(comboIdNum) ||
      comboIdNum <= 0 ||
      !effectiveObjectId
    ) {
      setComboEventDiscountPreview(null);
      return;
    }
    const code = (comboEventStripeDiscountCode || "").trim();
    const tid = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await previewComboEventDiscount({
            userId: effectiveObjectId,
            eventId: comboIdNum,
            discountCode: code,
            isEventEarlyBird: Boolean(comboEventEarlyBird),
          });
          setComboEventDiscountPreview(res);
        } catch {
          setComboEventDiscountPreview({
            valid: false,
            error: "Could not verify promotion code.",
          });
        }
      })();
    }, 480);
    return () => window.clearTimeout(tid);
  }, [
    showExplorerEventStripePromo,
    comboEventStripeDiscountCode,
    comboIdNum,
    comboEventEarlyBird,
    effectiveObjectId,
    previewComboEventDiscount,
  ]);

  useEffect(() => {
    const code = (discountCode || "").trim();
    const productId = membershipProductIdForCheckout;
    if (!effectiveObjectId || !productId) {
      setMembershipPromoPreview(null);
      setMembershipPromoPreviewError(null);
      return;
    }
    if (!earlyStageMembershipSynced) {
      setMembershipPromoPreview(null);
      setMembershipPromoPreviewError(null);
      return;
    }

    let cancelled = false;
    const tid = window.setTimeout(() => {
      void (async () => {
        if (!cancelled) {
          setMembershipPromoPreviewError(null);
          setMembershipPromoPreview(null);
        }
        try {
          const res = await previewWalletPricing({
            userId: effectiveObjectId,
            productId,
            ...(code ? { discountCode: code } : {}),
            isAnnual: isAnnualMembership === "annual",
            isEvent: false,
            isEventEarlyBird: false,
            name: `${userData.firstName || ""} ${userData.lastName || ""}`.trim(),
            email: typeof userData.email === "string" ? userData.email : "",
          });
          if (!cancelled) {
            setMembershipPromoPreviewError(null);
            setMembershipPromoPreview(res);
          }
        } catch (err: unknown) {
          if (!cancelled) {
            setMembershipPromoPreview(null);
            setMembershipPromoPreviewError(
              code ? getPaymentErrorMessage(err) : null,
            );
          }
        }
      })();
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(tid);
    };
  }, [
    discountCode,
    effectiveObjectId,
    selectedMembership,
    isAnnualMembership,
    memberships,
    membershipProductIdForCheckout,
    userData.firstName,
    userData.lastName,
    userData.email,
    earlyStageMembershipSynced,
  ]);

  useEffect(() => {
    let parsed: Record<string, unknown> = {};
    try {
      const raw = localStorage.getItem("UserData");
      if (raw) parsed = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      /* ignore corrupt storage */
    }
    setUserData(parsed as Record<string, any>);
    const bill =
      typeof parsed.billingAddress === "string"
        ? parsed.billingAddress.trim()
        : "";
    if (bill) setBillingAddress(bill);
  }, []);

  useEffect(() => {
    if (!effectiveObjectId) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;

    setLoading(true);
    axiosInstance
      .post(getAPIURL("/Payment/verify-checkout-session"), { sessionId })
      .then(() => {
        const formData = buildPaidMembershipFormData();
        persistRegistrationPaidMembershipPrice(confirmedMembershipPriceMajor);
        saveUserMutate(formData);
        window.history.replaceState({}, "", window.location.pathname);
      })
      .catch((err: unknown) => {
        setError(getPaymentErrorMessage(err));
      })
      .finally(() => setLoading(false));
  }, [
    effectiveObjectId,
    userData,
    discountCode,
    saveUserMutate,
    confirmedMembershipPriceMajor,
    buildPaidMembershipFormData,
  ]);

  const completeSubscriptionAfterCreateResponse = useCallback(
    async (
      response: CreatePaymentIntentResponse,
      pm: StripePaymentMethod | null | undefined,
    ) => {
      const runExplorerEventCheckoutAfterMembership = async () => {
        if (!shouldRunExplorerSequentialEventPayment) return;
        const evPromo = (comboEventStripeDiscountCode || "").trim();
        const eventResponse = await makeEventPayment({
          userId: effectiveObjectId,
          customerId: response.customerId ?? "",
          paymentMethodId: pm?.id,
          paymentMethodTypes: ["card"],
          productId: comboEventProductId.trim(),
          discountCode: evPromo,
          name: `${userData.firstName || ""} ${userData.lastName || ""}`.trim(),
          email: userData.email || "",
          isEvent: true,
          IsEventEarlyBird: Boolean(comboEventEarlyBird),
        });

        if (eventResponse.noPaymentRequired || eventResponse.paymentAlreadySucceeded) {
          sessionStorage.setItem(EXPLORER_EVENT_PAYMENT_DONE_KEY, "1");
          return;
        }

        const eventClientSecret = eventResponse.clientSecret;
        if (!eventClientSecret) {
          throw new Error("Event checkout client secret missing.");
        }

        const returnUrl = `${window.location.origin}${window.location.pathname}`;
        const { paymentIntent, error: eventConfirmError } =
          await stripe!.confirmCardPayment(eventClientSecret, {
            payment_method: pm?.id,
            return_url: returnUrl,
          });

        if (eventConfirmError) {
          throw new Error(
            eventConfirmError.message ||
              "Event payment confirmation failed after membership payment.",
          );
        }
        if (paymentIntent?.status !== "succeeded") {
          throw new Error("Event payment did not complete successfully.");
        }
        sessionStorage.setItem(EXPLORER_EVENT_PAYMENT_DONE_KEY, "1");
      };

      if (response.noPaymentRequired) {
        const formData = buildPaidMembershipFormData();
        if (showExplorerComboEventSection && !shouldRunExplorerSequentialEventPayment) {
          sessionStorage.setItem(EXPLORER_EVENT_PAYMENT_DONE_KEY, "1");
        }
        if (saveCard) {
          savePaymentMethod({
            userId: effectiveObjectId,
            customerId: response.customerId || "",
            paymentMethodId: pm?.id,
            billingAddress,
            brand: pm?.card?.brand || "",
            last4: pm?.card?.last4 || "",
          });
        }
        try {
          await runExplorerEventCheckoutAfterMembership();
        } catch (eventErr) {
          const msg =
            eventErr instanceof Error
              ? eventErr.message
              : "Membership is active, but event checkout failed. Please retry event checkout.";
          setError(msg);
          setLoading(false);
          return;
        }
        persistRegistrationPaidMembershipPrice(confirmedMembershipPriceMajor);
        saveUserMutate(formData);
        setLoading(false);
        return;
      }

      const clientSecret = response.clientSecret;
      if (!clientSecret) {
        const msg = "No client secret returned from server.";
        logPaymentError({
          flow: "membership",
          errorSource: "backend",
          message: msg,
          responseData: response,
        });
        setError(msg);
        setLoading(false);
        return;
      }

      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      persistRegistrationPaidMembershipPrice(confirmedMembershipPriceMajor);
      sessionStorage.setItem("registration_3ds_resume", "1");

      const usePaymentIntent = clientSecret.startsWith("pi_");
      let paymentOk = false;

      if (usePaymentIntent) {
        const { paymentIntent, error: confirmError } =
          await stripe!.confirmCardPayment(clientSecret, {
            payment_method: pm?.id,
            return_url: returnUrl,
          });

        if (confirmError) {
          if (confirmError.code === "payment_intent_unexpected_state") {
            paymentOk = true;
          } else {
            const isAuthFailure =
              confirmError.code === "payment_intent_authentication_failure";
            const msg = isAuthFailure
              ? "Authentication was cancelled or failed. Please try again or use another card."
              : confirmError.message || "Payment confirmation failed.";
            logPaymentError({
              flow: "membership",
              errorSource: "stripe",
              message: msg,
              error: confirmError,
            });
            setError(msg);
            setLoading(false);
            return;
          }
        } else if (paymentIntent?.status === "succeeded") {
          paymentOk = true;
        }
      } else {
        const { setupIntent, error: confirmError } =
          await stripe!.confirmCardSetup(clientSecret, {
            payment_method: pm?.id,
            return_url: returnUrl,
          });

        if (confirmError) {
          const isAuthFailure =
            confirmError.code === "setup_intent_authentication_failure";

          const msg = isAuthFailure
            ? "Authentication was cancelled or failed. Please try again or use another card."
            : confirmError.message || "Card setup confirmation failed.";

          logPaymentError({
            flow: "membership",
            errorSource: "stripe",
            message: msg,
            error: confirmError,
          });

          setError(msg);
          setLoading(false);
          return;
        }

        if (setupIntent && setupIntent.status === "succeeded") {
          paymentOk = true;
        }
      }

      if (paymentOk) {
        const formData = buildPaidMembershipFormData();
        if (showExplorerComboEventSection && !shouldRunExplorerSequentialEventPayment) {
          sessionStorage.setItem(EXPLORER_EVENT_PAYMENT_DONE_KEY, "1");
        }

        if (saveCard) {
          savePaymentMethod({
            userId: effectiveObjectId,
            customerId: response.customerId || "",
            paymentMethodId: pm?.id,
            billingAddress,
            brand: pm?.card?.brand || "",
            last4: pm?.card?.last4 || "",
          });
        }
        try {
          await runExplorerEventCheckoutAfterMembership();
        } catch (eventErr) {
          const msg =
            eventErr instanceof Error
              ? eventErr.message
              : "Membership is active, but event checkout failed. Please retry event checkout.";
          setError(msg);
          setLoading(false);
          return;
        }

        persistRegistrationPaidMembershipPrice(confirmedMembershipPriceMajor);
        saveUserMutate(formData);
      }

      setLoading(false);
    },
    [
      confirmedMembershipPriceMajor,
      stripe,
      shouldRunExplorerSequentialEventPayment,
      comboEventStripeDiscountCode,
      effectiveObjectId,
      makeEventPayment,
      comboEventProductId,
      comboEventEarlyBird,
      userData,
      discountCode,
      showExplorerComboEventSection,
      saveCard,
      savePaymentMethod,
      billingAddress,
      saveUserMutate,
      buildPaidMembershipFormData,
    ],
  );


  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      if (!stripe || !elements) {
        const msg =
          "The secure payment form is still loading. Please wait a moment and try again.";
        logPaymentError({
          flow: "membership",
          errorSource: "frontend",
          message: msg,
        });
        setError(msg);
        return;
      }

      setLoading(true);
      setError("");
      const selected = findMembershipForCheckout(memberships, selectedMembership, {
        earlyStageProductId: earlyStageProduct?.productId,
        earlyStageMonthlyFeeGbp: earlyStageProduct?.monthlyFeeGbp,
      });
      if (!selected && !membershipProductIdForCheckout) {
        const msg = t("checkout.invalidMembershipSelection");
        logPaymentError({
          flow: "membership",
          errorSource: "frontend",
          message: msg,
          requestPayload: {
            selectedMembership,
            membershipCount: memberships.length,
            membershipNames: memberships.map((m) => m.name),
          },
        });
        setError(msg);
        setLoading(false);
        return;
      }

      if (!effectiveObjectId) {
        const msg =
          t("checkout.accountIdMissing");
        logPaymentError({
          flow: "membership",
          errorSource: "frontend",
          message: msg,
        });
        setError(msg);
        setLoading(false);
        return;
      }

      try {
        const cardNumberElement = elements.getElement(CardNumberElement);
        if (!cardNumberElement) {
          const msg = t("checkout.cardInputNotFound");
          logPaymentError({
            flow: "membership",
            errorSource: "frontend",
            message: msg,
          });
          setError(msg);
          setLoading(false);
          return;
        }
        const { paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: cardNumberElement,
        });
        const flowTrim = explorerComboFlow.trim();
        const evPromo = (comboEventStripeDiscountCode || "").trim();
        if (showExplorerEventStripePromo && evPromo && !comboEventDiscountPreview?.valid) {
          setError(
            t("events.enterValidPromoOrClear"),
          );
          setLoading(false);
          return;
        }
        const membershipPromoTrimmed = (discountCode || "").trim();
        if (membershipPromoTrimmed && membershipPromoPreviewError) {
          setError(membershipPromoPreviewError);
          setLoading(false);
          return;
        }
        const comboPayload =
          Number.isFinite(comboIdNum) &&
          comboIdNum > 0 &&
          !shouldRunExplorerSequentialEventPayment
            ? {
                comboEventId: comboIdNum,
                comboEventEarlyBird: Boolean(comboEventEarlyBird),
                ...(flowTrim ? { explorerFlow: flowTrim } : {}),
                ...(evPromo ? { comboEventStripeDiscountCode: evPromo } : {}),
              }
            : {};

        if (earlyStageGateMessage) {
          setError(earlyStageGateMessage);
          setLoading(false);
          return;
        }
        if (!membershipProductIdForCheckout) {
          setError(
            needsEarlyStagePricePlanSelection(earlyStageStatus)
              ? "Please choose your £2 or £10 monthly payment on the Payment Plan step first."
              : earlyStageDirectStandardPath
                ? "Membership payment is not ready. Please try again or contact support."
                : "Membership payment is not ready. Complete membership verification first."
          );
          setLoading(false);
          return;
        }

        persistRegistrationPaidMembershipPrice(confirmedMembershipPriceMajor);

        try {
          const response = await createSubscriptionAsync({
            productId: membershipProductIdForCheckout,
            userId: effectiveObjectId,
            paymentMethodId: paymentMethod?.id,
            discountCode: discountCode || "",
            name: `${userData.firstName || ""} ${userData.lastName || ""}`.trim(),
            email: userData.email || "",
            ...comboPayload,
          });
          await completeSubscriptionAfterCreateResponse(response, paymentMethod ?? null);
        } catch (err: unknown) {
          const source = getPaymentErrorSource(err);
          const msg = getPaymentErrorMessage(err);
          logPaymentError({
            flow: "membership",
            errorSource: source,
            message: msg,
            error: err,
            responseStatus:
              err && typeof err === "object" && "response" in err
                ? (err as { response?: { status?: number } }).response
                    ?.status
                : undefined,
            responseData:
              err && typeof err === "object" && "response" in err
                ? (err as { response?: { data?: unknown } }).response?.data
                : undefined,
          });
          setError(msg);
          setLoading(false);
        }
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : t("events.unexpectedError");
        logPaymentError({
          flow: "membership",
          errorSource: "frontend",
          message: msg,
          error: err,
        });
        setError(msg);
        setLoading(false);
      }
    },
    [
      stripe,
      elements,
      memberships,
      selectedMembership,
      isAnnualMembership,
      userData,
      effectiveObjectId,
      discountCode,
      saveCard,
      billingAddress,
      createSubscriptionAsync,
      completeSubscriptionAfterCreateResponse,
      saveUserMutate,
      savePaymentMethod,
      comboEventEarlyBird,
      explorerComboFlow,
      comboIdNum,
      comboEventStripeDiscountCode,
      showExplorerEventStripePromo,
      comboEventDiscountPreview,
      shouldRunExplorerSequentialEventPayment,
      showExplorerComboEventSection,
      comboEventProductId,
      makeEventPayment,
      membershipPromoPreviewError,
      earlyStageProduct,
      membershipProductIdForCheckout,
      earlyStageGateMessage,
      earlyStageStatus,
      confirmedMembershipPriceMajor,
      t,
    ]
  );

  const handleMembershipHostedCheckout = useCallback(
    async (preferredWallet?: "apple_pay" | "google_pay") => {
      if (!preferredWallet) return;

      const walletMessage = getUnsupportedWalletMessage(preferredWallet);
      if (walletMessage) {
        setError(walletMessage);
        return;
      }

      const selected = findMembershipForCheckout(memberships, selectedMembership, {
        earlyStageProductId: earlyStageProduct?.productId,
        earlyStageMonthlyFeeGbp: earlyStageProduct?.monthlyFeeGbp,
      });
      if ((!selected && !membershipProductIdForCheckout) || !effectiveObjectId || !stripe) {
        return;
      }
      const membershipProductId = membershipProductIdForCheckout;
      if (!membershipProductId) return;
      if (earlyStageGateMessage) {
        setError(earlyStageGateMessage);
        return;
      }

      const flowTrim = explorerComboFlow.trim();
      const evPromo = (comboEventStripeDiscountCode || "").trim();
      if (
        showExplorerEventStripePromo &&
        evPromo &&
        !comboEventDiscountPreview?.valid
      ) {
        setError(
          "Enter a valid event promotion code for the event ticket or clear the field.",
        );
        return;
      }

      const membershipPromoTrimmed = (discountCode || "").trim();
      if (membershipPromoTrimmed && membershipPromoPreviewError) {
        setError(membershipPromoPreviewError);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const preview = await previewWalletPricing({
          userId: effectiveObjectId,
          productId: membershipProductId,
          discountCode: discountCode || undefined,
          isAnnual: isAnnualMembership === "annual",
          isEvent: false,
          isEventEarlyBird: false,
          name: `${userData.firstName || ""} ${userData.lastName || ""}`.trim(),
          email: userData.email || "",
        });

        if (preview.noPaymentRequired) {
          setError(
            "No payment is due for this selection. Please use Pay Now below.",
          );
          setLoading(false);
          return;
        }

        const comboPayload =
          Number.isFinite(comboIdNum) &&
          comboIdNum > 0 &&
          !shouldRunExplorerSequentialEventPayment
            ? {
                comboEventId: comboIdNum,
                comboEventEarlyBird: Boolean(comboEventEarlyBird),
                ...(flowTrim ? { explorerFlow: flowTrim } : {}),
                ...(evPromo ? { comboEventStripeDiscountCode: evPromo } : {}),
              }
            : {};

        await runWalletPaymentRequest({
          stripe,
          amountMinor: preview.amountMinor,
          currency: preview.currency,
          label: preview.label,
          preferredWallet,
          onPaymentMethod: async (pm) => {
            const response = await createSubscriptionAsync({
              productId: membershipProductId,
              userId: effectiveObjectId,
              paymentMethodId: pm.id,
              discountCode: discountCode || "",
              name: `${userData.firstName || ""} ${userData.lastName || ""}`.trim(),
              email: userData.email || "",
              ...comboPayload,
            });
            await completeSubscriptionAfterCreateResponse(response, pm);
          },
        });
      } catch (err: unknown) {
        const msg = getPaymentErrorMessage(err);
        if (
          msg === "cancelled" ||
          (err instanceof Error && err.message === "cancelled")
        ) {
          setError("");
        } else {
          setError(msg);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      memberships,
      selectedMembership,
      effectiveObjectId,
      stripe,
      isAnnualMembership,
      discountCode,
      userData,
      explorerComboFlow,
      comboEventStripeDiscountCode,
      showExplorerEventStripePromo,
      comboEventDiscountPreview,
      comboIdNum,
      comboEventEarlyBird,
      shouldRunExplorerSequentialEventPayment,
      createSubscriptionAsync,
      completeSubscriptionAfterCreateResponse,
      membershipPromoPreviewError,
    ],
  );

  return (
    <form onSubmit={handleSubmit} className="w-100" autoComplete="off">
      <div
        className="d-flex flex-column gap-3"
        style={CONTAINER_STYLE}
      >
        {isEarlyStageCheckout &&
          !hideEarlyStagePriceChoice &&
          needsEarlyStagePricePlanSelection(earlyStageStatus) && (
          <EarlyStagePriceChoice
            onComplete={(fee) => {
              setEarlyStageChosenFee(fee);
              void refreshEarlyStageCheckout();
            }}
          />
        )}

        {isEarlyStageCheckout && earlyStageGateMessage && (
          <div className="alert alert-warning mb-0" role="status">
            {earlyStageGateMessage}
          </div>
        )}

        <div className="d-flex justify-content-between">
          <h5>{tt("checkout")}</h5>
          <h5>
            {isAnnualMembership === "annual"
              ? tt("annualMembership")
              : tt("monthlyMembership")}
          </h5>
        </div>

        {shouldRunExplorerSequentialEventPayment && (
          <div
            className="alert alert-info small py-2 px-3 mb-0"
            role="status"
          >
            <strong>Two charges, one click.</strong> Your membership is charged
            first (this may appear on your statement as a subscription or
            recurring payment). Your event ticket is charged next (a separate{" "}
            <strong>payment</strong>
            ). Both use this card — seeing two separate lines on your bank or
            card statement is normal.
            {!membershipAndEventSameCurrency && (
              <>
                {" "}{t("checkout.eventAnotherCurrency")} <strong>{t("checkout.indicative")}</strong> {t("checkout.totalCurrencyConvenience")}
              </>
            )}
          </div>
        )}

        {fxError && shouldRunExplorerSequentialEventPayment && (
          <div className="alert alert-warning small py-2 px-3 mb-0" role="status">
            {fxError}
          </div>
        )}

        <div className="px-2 border-top pt-2">
          <div className="d-flex justify-content-between">
            <span className="fw-medium">
              {selectedMembershipLabel} (
              {isAnnualMembership === "annual"
                ? t("account.billingCycle.annual")
                : t("account.billingCycle.monthly")}
              )
            </span>
            <div className="text-end">
            {shouldRunExplorerSequentialEventPayment &&
            !membershipAndEventSameCurrency &&
            fxLoading ? (
              <span className="fw-bold text-muted small">
                {t("checkout.loadingIndicativeRate")}
              </span>
            ) : shouldRunExplorerSequentialEventPayment &&
              !membershipAndEventSameCurrency &&
              membershipShownInEventCurrency != null ? (
              <>
                <span className="fw-bold">
                  {formatCurrencyAmount(
                    membershipShownInEventCurrency,
                    comboCurrency,
                  )}
                </span>
                <span className="d-block small text-muted">
                  Billed as{" "}
                  {formatCurrencyAmount(
                    showMembershipPromoBreakdown
                      ? membershipListPriceMajor
                      : effectiveMembershipPrice,
                    membershipBillingCurrency,
                  )}
                </span>
              </>
            ) : shouldRunExplorerSequentialEventPayment ? (
              <span className="fw-bold">
                {formatCurrencyAmount(
                  showMembershipPromoBreakdown
                    ? membershipListPriceMajor
                    : effectiveMembershipPrice,
                  membershipBillingCurrency,
                )}
              </span>
            ) : showMembershipPromoBreakdown ? (
              <span className="fw-bold">
                {formatCurrencyAmount(
                  membershipListPriceMajor,
                  membershipBillingCurrency,
                )}
              </span>
            ) : (
              <span className="fw-bold">
                {formatCurrencyAmount(
                  effectiveMembershipPrice,
                  membershipBillingCurrency,
                )}
              </span>
            )}
          </div>
          </div>
          {showMembershipPromoBreakdown && (
            <div className="d-flex justify-content-between text-success small mt-1">
              <span className="pe-2">
                Membership promotion
                {(discountCode || "").trim()
                  ? ` (${(discountCode || "").trim()})`
                  : ""}
              </span>
              <span className="text-nowrap flex-shrink-0">
                −
                {formatCurrencyAmount(
                  membershipPromoDiscountMajor,
                  membershipBillingCurrency,
                )}
              </span>
            </div>
          )}
          {membershipPromoSummary && (
            <p className="small text-muted mb-0 mt-1">{membershipPromoSummary}</p>
          )}
          {membershipPromoPreviewError && (
            <Form.Text className="text-danger d-block mt-1">
              {membershipPromoPreviewError}
            </Form.Text>
          )}

        {showExplorerComboEventSection && (
          <div className="pt-2">
            {            comboEventEarlyBirdDiscountAmount <= 0 &&
            comboEventMemberDiscountAmount <= 0 ? (
              <div className="d-flex justify-content-between">
                <span className="fw-medium">
                  {t("events.title")}: {comboEventName || t("checkout.selectedEvent")}
                </span>
                <span className="fw-bold">
                  {formatCurrencyAmount(comboDisplayPrice, comboCurrency)}
                </span>
              </div>
            ) : (
              <>
                <div className="small text-muted mb-1">{t("checkout.eventTicket")}</div>
                <div className="d-flex justify-content-between">
                  <span className="fw-medium">
                    {comboEventName || "Selected event"}
                  </span>
                  <span className="fw-bold">
                    {formatCurrencyAmount(comboEventCatalogPrice, comboCurrency)}
                  </span>
                </div>
                {comboEventEarlyBird && comboEventEarlyBirdDiscountAmount > 0 && (
                  <div className="d-flex justify-content-between text-success small mt-1">
                    <span>{t("checkout.earlyBirdBeforeEndDate")}</span>
                    <span className="text-nowrap">
                      −
                      {formatCurrencyAmount(
                        comboEventEarlyBirdDiscountAmount,
                        comboCurrency,
                      )}
                    </span>
                  </div>
                )}
                {comboEventMemberDiscountAmount > 0 &&
                  comboEventMemberDiscountLabel && (
                    <div className="d-flex justify-content-between text-success small mt-1">
                      <span className="pe-2">
                        {comboEventMemberDiscountLabel}
                      </span>
                      <span className="text-nowrap flex-shrink-0">
                        −
                        {formatCurrencyAmount(
                          comboEventMemberDiscountAmount,
                          comboCurrency,
                        )}
                      </span>
                    </div>
                  )}
                {(comboEventStripeDiscountCode || "").trim() &&
                  comboEventDiscountPreview?.valid &&
                  (comboEventDiscountPreview.couponDiscountAmount ?? 0) >
                    0 && (
                    <div className="d-flex justify-content-between text-success small mt-1">
                      <span>{t("events.eventPromotion")}</span>
                      <span className="text-nowrap">
                        −
                        {formatCurrencyAmount(
                          comboEventDiscountPreview.couponDiscountAmount ?? 0,
                          comboCurrency,
                        )}
                      </span>
                    </div>
                  )}
                <div className="d-flex justify-content-between mt-2 pt-1 border-top">
                  <span className="fw-medium">Ticket total</span>
                  <span className="fw-bold">
                    {formatCurrencyAmount(comboDisplayPrice, comboCurrency)}
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {showExplorerComboEventSection && showExplorerEventStripePromo && (
            <div className="pt-2 mt-1">
              {!showComboEventPromo ? (
                <button
                  type="button"
                  className="btn btn-link p-0 text-start border-0 bg-transparent text-primary text-decoration-underline"
                  onClick={() => setShowComboEventPromo(true)}
                >
                  {t("events.havePromotionCode")}
                </button>
              ) : (
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-1">
                    {t("events.eventPromotionCode")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    autoComplete="off"
                    value={comboEventStripeDiscountCode || ""}
                    onChange={(e) =>
                      onComboEventStripeDiscountCodeChange?.(e.target.value)
                    }
                    placeholder="Enter your promotion code"
                  />
                  {(comboEventStripeDiscountCode || "").trim().length > 0 &&
                    comboEventDiscountPreview &&
                    comboEventDiscountPreview.valid === false &&
                    comboEventDiscountPreview.error && (
                      <Form.Text className="text-danger d-block">
                        {comboEventDiscountPreview.error}
                      </Form.Text>
                    )}
                </Form.Group>
              )}
            </div>
          )}

        {showCheckoutTotal &&
          (shouldRunExplorerSequentialEventPayment ? (
            <div className="border-top pt-2 mt-2">
              <div className="small text-muted mb-1">How you’ll be charged</div>
              <div className="d-flex justify-content-between">
                <span className="fw-medium">{t("checkout.membershipSubscription")}</span>
                <div className="text-end">
                  {!membershipAndEventSameCurrency &&
                  membershipShownInEventCurrency != null ? (
                    <>
                      <span className="fw-bold">
                        {formatCurrencyAmount(
                          membershipShownInEventCurrency,
                          comboCurrency,
                        )}
                      </span>
                      <span className="d-block small text-muted">
                        (
                        {formatCurrencyAmount(
                          effectiveMembershipPrice,
                          membershipBillingCurrency,
                        )}
                        )
                      </span>
                    </>
                  ) : (
                    <span className="fw-bold">
                      {formatCurrencyAmount(
                        effectiveMembershipPrice,
                        membershipBillingCurrency,
                      )}
                    </span>
                  )}
                </div>
              </div>
              <div className="d-flex justify-content-between mt-1">
                <span className="fw-medium">{t("checkout.eventTicketLine")}</span>
                <span className="fw-bold">
                  {formatCurrencyAmount(comboDisplayPrice, comboCurrency)}
                </span>
              </div>
              {membershipAndEventSameCurrency ? (
                <div className="d-flex justify-content-between border-top pt-2 mt-2">
                  <span className="fw-medium">You pay today</span>
                  <span className="fw-bold">
                    {formatCurrencyAmount(checkoutTotalToday, comboCurrency)}
                  </span>
                </div>
              ) : totalShownInEventCurrency != null ? (
                <>
                  <div className="d-flex justify-content-between border-top pt-2 mt-2">
                    <span className="fw-medium">
                      {t("checkout.indicativeTotal")} ({comboCurrency.toUpperCase()})
                    </span>
                    <span className="fw-bold">
                      {formatCurrencyAmount(
                        totalShownInEventCurrency,
                        comboCurrency,
                      )}
                    </span>
                  </div>
                  {fxRateDate && (
                    <p className="small text-muted mb-0 mt-1">
                      Conversion uses ECB-published rates (via Frankfurter) for
                      display only, rate date {fxRateDate}. Your bank may use a
                      different rate; membership is still billed in GBP and the
                      event in {comboCurrency.toUpperCase()}.
                    </p>
                  )}
                </>
              ) : (
                <p className="small text-muted mb-0 mt-2">
                  {t("checkout.membershipChargedInGbp", { currency: comboCurrency.toUpperCase() })}
                </p>
              )}
            </div>
          ) : (
              <div className="d-flex justify-content-between border-top pt-2 mt-2">
                <span className="fw-medium">{t("checkout.youPayToday")}</span>
                <span className="fw-bold">
                  {formatCurrencyAmount(
                    checkoutTotalToday,
                    showExplorerComboEventSection
                      ? comboCurrency
                      : membershipBillingCurrency,
                  )}
                </span>
              </div>
          ))}
        </div>

        <hr />

        <div className="row g-2">
          <div className="col-md-6 mb-3">
            <label>{tt("cardNumber")}</label>
            <div className="form-control">
              <CardNumberElement options={CARD_NUMBER_OPTIONS} />
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <label>{tt("expiry")}</label>
            <div className="form-control">
              <CardExpiryElement options={CARD_EXPIRY_OPTIONS} />
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <label>{tt("cvc")}</label>
            <div className="form-control">
              <CardCvcElement options={CARD_CVC_OPTIONS} />
            </div>
          </div>

          <div className="col-md-6 mb-3">
            <label>{tt("billingAddressOptional")}</label>
            <input
              type="text"
              className="form-control"
              placeholder={tt("billingAddress")}
              value={billingAddress}
              autoComplete="new-address"
              onChange={(e) => setBillingAddress(e.target.value)}
            />
          </div>

          <div className="col-md-12 mb-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="saveCard"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="saveCard">
                {tt("saveCardForFuture")}
              </label>
            </div>
          </div>
        </div>

        <div className="rounded-3 p-2 mt-2 bg-white">
          <div className="d-flex align-items-center justify-content-start gap-3">
            {showApplePay && (
            <button
              type="button"
              className="btn btn-dark border border-secondary text-white fw-semibold d-flex align-items-center justify-content-center flex-fill rounded-4 px-4 py-3 shadow-sm"
              onClick={() => void handleMembershipHostedCheckout("apple_pay")}
              disabled={
                loading ||
                isSaveUserPending ||
                !effectiveObjectId ||
                showExplorerComboEventSection
              }
            >
              <span className="d-inline-flex align-items-center justify-content-center gap-2">
                <span className="d-inline-flex align-items-center justify-content-center gap-1">
                  <FontAwesomeIcon
                    icon={faApple}
                    className="fs-4"
                  />
                  <span className="fs-3 fw-medium lh-1 text-white">
                    {tt("pay")}
                  </span>
                </span>
              </span>
            </button>
            )}
            {showGooglePay && (
            <button
              type="button"
              className="btn btn-light bg-white border border-secondary-subtle fw-semibold d-flex align-items-center justify-content-center flex-fill rounded-4 px-4 py-3 shadow-sm"
              onClick={() => void handleMembershipHostedCheckout("google_pay")}
              disabled={
                loading ||
                isSaveUserPending ||
                !effectiveObjectId ||
                showExplorerComboEventSection
              }
            >
              <span className="d-inline-flex align-items-center justify-content-center gap-2">
                <span className="d-inline-flex align-items-center justify-content-center gap-1">
                  <FcGoogle className="fs-4" />
                  <span className="fs-3 fw-medium lh-1 text-dark">
                    {tt("pay")}
                  </span>
                </span>
              </span>
            </button>
            )}
          </div>
        </div>

        {error && (
          <div className="alert alert-danger py-2 px-3" role="alert">
            {error}
          </div>
        )}

        <div className="d-flex justify-content-between mt-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onBack}
            disabled={loading}
          >
            {t("auth.back")}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              !stripe ||
              loading ||
              isSaveUserPending ||
              (shouldRunExplorerSequentialEventPayment &&
                !membershipAndEventSameCurrency &&
                fxLoading)
            }
          >
            {loading || isSaveUserPending
              ? tt("processing")
              : shouldRunExplorerSequentialEventPayment &&
                  !membershipAndEventSameCurrency &&
                  fxLoading
                ? t("checkout.loadingRate")
              : shouldRunExplorerSequentialEventPayment
                ? t("checkout.payMembershipAndEvent")
                : tt("payNow")}
          </button>
        </div>
      </div>
    </form>
  );
}

CheckoutStepInner.displayName = "CheckoutStep";

const CheckoutStep = memo(CheckoutStepInner);
CheckoutStep.displayName = "CheckoutStep";

export default CheckoutStep;




